import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CurrencyConverterMini from './CurrencyConverterMini';
import CalculatorTool from './tools/CalculatorTool';
import NotesTool from './tools/NotesTool';
import VisaTool from './tools/VisaTool'; // V70.149 — MFA Vize Bilgisi
import './FloatingTools.css';

/* V70.148 — Hızlı Araçlar (Floating Tools):
 * Ekran kenarına yapışan, dikeyde sürüklenebilir, kenar değiştirince snap olan global FAB.
 * Kapalı: yarım daire; hover (dokunmatikte tap): araçlar yelpaze/radial açılır.
 * Araç tıklanınca sayfanın üzerinde modal açılır — rota değişmez, sayfa state'i korunur.
 * Konum kullanıcı bazlı localStorage'da tutulur; araç listesi yetkiye göre süzülür.
 * Yeni araç eklemek için TOOLS listesine kayıt + render bloğu yeterli. */

const POS_KEY = (uid) => `mtu_ft_pos:${uid}`;

/* V71 — sağ kenarda alt bölge "Fikrim Var" butonuna ayrılır: FAB oraya yapışıp üst üste binmesin */
const clampTop = (side, pct) => {
  const h = window.innerHeight || 800;
  const max = side === 'right' ? Math.min(88, ((h - 116) / h) * 100) : 88;
  return Math.min(max, Math.max(8, pct));
};

const readPos = (uid) => {
  try {
    const raw = localStorage.getItem(POS_KEY(uid));
    if (raw) {
      const p = JSON.parse(raw);
      if ((p.side === 'left' || p.side === 'right') && typeof p.topPct === 'number') {
        return { side: p.side, topPct: clampTop(p.side, p.topPct) };
      }
    }
  } catch {}
  return { side: 'right', topPct: 62 };
};

/* Araç kaydı — perm: [module, action] verilirse hasPermission ile süzülür */
const TOOLS = [
  { id: 'calc', label: 'Hesap Makinesi', icon: '🧮', width: 340 },
  { id: 'currency', label: 'Kur Çevirici', icon: '💱', width: 440, perm: ['currency_converter', 'view'] },
  { id: 'visa', label: 'Vize Bilgisi', icon: '🛂', width: 560 }, // V70.149 — MFA ülke/pasaport vize durumu
  { id: 'notes', label: 'Notlarım', icon: '📝', width: 480 },
];

const FAN_RADIUS = 104; // V70.150 — 60px FAB + 46px item'a göre

/* V70.156 — olay fare/kalemden mi geliyor? (pointerType boş bırakan eski
 * tarayıcılarda fare varsayılır; dokunma için tap toggle zaten var) */
const isHoverPointer = (e) => {
  const t = e && e.pointerType;
  return !t || t === 'mouse' || t === 'pen';
};

/* V70.150 — tanıtım modalındaki kısa açıklamalar */
const INTRO_DESC = {
  calc: 'Dört işlem + yüzde, klavye destekli, Türkçe sayı biçimi',
  currency: 'TCMB + çapraz kurlar — artık her sayfada tek hamlede',
  visa: 'Ülke ve pasaport türüne göre vize durumu (Dışişleri verisi)',
  notes: 'Yazdıkça otomatik kaydolan kişisel karalama defteri',
};

/* V70.150 — özenli FAB ikonu: araç ızgarası + parıltı (aynı ikon tanıtım modalında da) */
const FabIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.2" y="3.2" width="7.4" height="7.4" rx="2.2" />
    <rect x="13.4" y="3.2" width="7.4" height="7.4" rx="2.2" />
    <rect x="3.2" y="13.4" width="7.4" height="7.4" rx="2.2" />
    <path d="M17.1 12.3l1.1 3.2 3.2 1.1-3.2 1.1-1.1 3.2-1.1-3.2-3.2-1.1 3.2-1.1z" fill="currentColor" stroke="none" />
  </svg>
);

const FloatingTools = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const uid = user?.id ?? user?.username ?? 'u';

  const [pos, setPos] = useState(() => readPos(uid));
  const [open, setOpen] = useState(false);
  /* V70.151 — performans: sürükleme koordinatları STATE DEĞİL (her pointermove'da
   * re-render + left/top layout'u takılma yaratıyordu). Koordinatlar dragRef'te,
   * konum rAF ile doğrudan DOM'a translate3d yazılır; state yalnız başla/bitir bilir. */
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(null); // açık araç id'si
  /* V70.150 — keşif balonu (ilk 48 saat) + ilk tıklamada tanıtım modalı */
  const [introOpen, setIntroOpen] = useState(false);
  const [introSeen, setIntroSeen] = useState(true);   // localStorage'dan effect'te okunur
  const [balloonOff, setBalloonOff] = useState(true);
  const [discovery, setDiscovery] = useState(false);  // ilk görüşten itibaren 48 saat

  const wrapRef = useRef(null);
  const dragRef = useRef(null);          // {startX, startY, moved}
  const suppressClick = useRef(false);   // drag sonrası click'i yut
  const closeTimer = useRef(null);

  useEffect(() => { setPos(readPos(uid)); }, [uid]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* V70.150 — keşif penceresi: ilk görüş anını damgala, 48 saat boyunca balon göster */
  useEffect(() => {
    try {
      let t = Number(localStorage.getItem(`mtu_ft_seen_at:${uid}`));
      if (!t) { t = Date.now(); localStorage.setItem(`mtu_ft_seen_at:${uid}`, String(t)); }
      setDiscovery(Date.now() - t < 48 * 3600 * 1000);
      setIntroSeen(localStorage.getItem(`mtu_ft_intro_seen:${uid}`) === '1');
      setBalloonOff(localStorage.getItem(`mtu_ft_balloon_off:${uid}`) === '1');
    } catch { setDiscovery(false); }
  }, [uid]);

  const markIntroSeen = useCallback(() => {
    setIntroSeen(true);
    try { localStorage.setItem(`mtu_ft_intro_seen:${uid}`, '1'); } catch {}
  }, [uid]);

  const dismissBalloon = () => {
    setBalloonOff(true);
    try { localStorage.setItem(`mtu_ft_balloon_off:${uid}`, '1'); } catch {}
  };

  const openIntro = useCallback(() => {
    markIntroSeen();
    setOpen(false);
    setIntroOpen(true);
  }, [markIntroSeen]);

  /* Dışarı tıklayınca yelpazeyi kapat (dokunmatik ana yol) */
  useEffect(() => {
    if (!open || active) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, active]);

  /* Esc → yelpazeyi kapat (modal Esc'i AppLayout'un global overlay-click'i yönetir) */
  useEffect(() => {
    if (!open || active) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, active]);

  /* ── Sürükleme (pointer capture; 5px eşiği altı = tıklama) ──
   * Hareket rAF-birleştirmeli olarak DOMʼa doğrudan yazılır (translate3d → compositor);
   * pointermove başına React render YOK. */
  const applyDragXY = () => {
    const d = dragRef.current;
    const el = wrapRef.current;
    if (!d || !el) return;
    el.style.left = '0px';
    el.style.top = '0px';
    el.style.right = 'auto';
    el.style.transform = `translate3d(${d.x - 30}px, ${d.y - 30}px, 0)`;
  };

  const clearDragStyles = () => {
    const el = wrapRef.current;
    if (el) { el.style.left = ''; el.style.top = ''; el.style.right = ''; el.style.transform = ''; }
  };

  const onFabPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY, moved: false, raf: 0 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };

  const onFabPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.x = e.clientX;
    d.y = e.clientY;
    if (!d.moved) {
      if (Math.abs(e.clientX - d.startX) < 5 && Math.abs(e.clientY - d.startY) < 5) return;
      d.moved = true;
      setOpen(false);
      setDragging(true);
      applyDragXY(); // ilk kare beklemeden tutulsun
    }
    if (!d.raf) {
      d.raf = requestAnimationFrame(() => {
        const dd = dragRef.current;
        if (dd) dd.raf = 0;
        applyDragXY();
      });
    }
  };

  const onFabPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.raf) cancelAnimationFrame(d.raf);
    if (d && d.moved) {
      suppressClick.current = true;
      // React'in style diff'i imperative yazılan stilleri bilmez — dock stiline
      // dönmeden önce inline left/top/transform mutlaka elle temizlenir
      clearDragStyles();
      const side = e.clientX < window.innerWidth / 2 ? 'left' : 'right';
      const topPct = clampTop(side, (e.clientY / window.innerHeight) * 100);
      const next = { side, topPct };
      setDragging(false);
      setPos(next);
      try { localStorage.setItem(POS_KEY(uid), JSON.stringify(next)); } catch {}
    }
  };

  const onFabPointerCancel = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && d.raf) cancelAnimationFrame(d.raf);
    clearDragStyles();
    setDragging(false);
  };

  const onFabClick = () => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (!introSeen) { openIntro(); return; } // V70.150 — ilk tıklama tanıtım modalını açar
    setOpen(o => !o);
  };

  /* ── Hover ile aç/kapa ──
   * V70.156 — ESKİ kapı `matchMedia('(hover: hover)')` idi. Bu sorgu BİRİNCİL
   * girdiyi bildirir: dokunmatik ekranlı Windows dizüstülerde (kullanıcının
   * makinesi 10 noktalı dokunmatik) tarayıcı 'coarse' diyor → sorgu false →
   * FARE kullanan kullanıcıda hover TAMAMEN devre dışı kalıyor, yelpaze yalnız
   * tıklamayla açılıyordu (kullanıcı bildirimi; aynı makinede canlı doğrulandı:
   * hover=false, any-hover=false, maxTouchPoints=10).
   * YENİ: karar medya sorgusundan değil GERÇEK OLAYDAN verilir — pointerenter'ın
   * pointerType'ı 'mouse'/'pen' ise hover, 'touch' ise dokunma sayılır (tap
   * toggle'ı onClick zaten yapıyor). Böylece cihaz ne bildirirse bildirsin fare
   * hover'ı çalışır. */
  const onEnter = useCallback((e) => {
    if (!isHoverPointer(e) || dragRef.current) return;
    clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);

  const onLeave = useCallback((e) => {
    clearTimeout(closeTimer.current);
    if (!isHoverPointer(e)) return; // dokunmada yelpaze tapla kapanır
    closeTimer.current = setTimeout(() => setOpen(false), 260);
  }, []);

  /* ── Yelpaze geometrisi: dock kenarının tersine; üst/alt kenara yakınsa açı kaydırılır ── */
  const tools = TOOLS.filter(t => !t.perm || hasPermission(t.perm[0], t.perm[1]));
  const n = tools.length;
  const bias = pos.topPct < 24 ? 40 : pos.topPct > 76 ? -40 : 0; // derece; + = aşağı
  const span = n <= 1 ? 0 : Math.min(150, 50 * (n - 1));
  const items = tools.map((t, i) => {
    const deg = n === 1 ? bias : bias - span / 2 + (span * i) / (n - 1);
    const rad = (deg * Math.PI) / 180;
    const inward = Math.cos(rad) * FAN_RADIUS;
    return {
      ...t,
      dx: pos.side === 'right' ? -inward : inward,
      dy: Math.sin(rad) * FAN_RADIUS,
      delay: i * 40,
    };
  });

  const fanVisible = open && !dragging;
  const activeTool = tools.find(t => t.id === active) || null;

  /* Drag sırasında olası bir re-render (örn. TopBar poll'u) imperative stilleri
   * ezerse diye stil ref'teki son koordinattan üretilir — imperative yazımla birebir. */
  const wrapStyle = dragging
    ? (() => {
        const d = dragRef.current;
        return { left: 0, top: 0, right: 'auto', transform: `translate3d(${(d ? d.x : 0) - 30}px, ${(d ? d.y : 0) - 30}px, 0)` };
      })()
    : pos.side === 'right'
      ? { right: 0, top: `${pos.topPct}%`, transform: 'translateY(-50%)' }
      : { left: 0, top: `${pos.topPct}%`, transform: 'translateY(-50%)' };

  const openTool = (id) => {
    markIntroSeen(); // araç kullanan kullanıcıyı tanıtımla tekrar rahatsız etme
    setOpen(false);
    setActive(id);
  };

  const balloonVisible = discovery && !balloonOff && !introSeen && !open && !dragging && !active && !introOpen;

  return createPortal(
    <>
      <div
        ref={wrapRef}
        className={`ft-wrap${fanVisible ? ' ft-open' : ''}`}
        data-side={pos.side}
        data-drag={dragging ? '1' : undefined}
        style={wrapStyle}
        onPointerEnter={onEnter}
        onPointerLeave={onLeave}
      >
        {fanVisible && <div className="ft-bridge" onClick={() => setOpen(false)} />}

        {/* V70.150 — keşif balonu: ilk 48 saat, çarpıyla kapanır, tıklayınca tanıtım */}
        {balloonVisible && (
          <div className={`ft-balloon ft-balloon-${pos.side}${pos.topPct > 74 ? ' ft-balloon-up' : ''}`} onClick={openIntro} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIntro(); } }}>
            <button className="ft-balloon-x" onClick={(e) => { e.stopPropagation(); dismissBalloon(); }} aria-label="Kapat" title="Kapat">×</button>
            <div className="ft-balloon-title">🛠️ Hızlı Araçlar burada!</div>
            <div className="ft-balloon-text">Hesap makinesi, kur çevirici, vize bilgisi ve notlar. Tanımak için tıkla.</div>
          </div>
        )}
        {items.map((t, i) => (
          <button
            key={t.id}
            className="ft-item"
            style={fanVisible
              ? { transform: `translate(${t.dx}px, ${t.dy}px)`, opacity: 1, pointerEvents: 'auto', transitionDelay: `${t.delay}ms` }
              : { transform: 'translate(0px, 0px) scale(0.4)', opacity: 0, pointerEvents: 'none', transitionDelay: `${(items.length - 1 - i) * 25}ms` }}
            onClick={() => openTool(t.id)}
            aria-label={t.label}
          >
            <span className="ft-item-ic">{t.icon}</span>
            <span className={`ft-label ft-label-${pos.side}`}>{t.label}</span>
          </button>
        ))}
        <button
          className="ft-fab"
          title="Hızlı Araçlar — sürükleyerek taşıyabilirsin"
          aria-label="Hızlı Araçlar"
          onPointerDown={onFabPointerDown}
          onPointerMove={onFabPointerMove}
          onPointerUp={onFabPointerUp}
          onPointerCancel={onFabPointerCancel}
          onClick={onFabClick}
        >
          <FabIcon />
        </button>
      </div>

      {/* ── V70.150: Tanıtım modalı (ilk tıklamada bir kez) ── */}
      {introOpen && (
        <div className="modal-overlay" onClick={() => setIntroOpen(false)}>
          <div className="modal modal-sm ft-intro" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" style={{ overflow: 'hidden' }}>
            <div className="ft-intro-hero">
              <button className="ft-intro-x" onClick={() => setIntroOpen(false)} title="Kapat" aria-label="Kapat">×</button>
              <div className="ft-intro-fab"><FabIcon size={30} /></div>
              <h3>Hızlı Araçlar</h3>
              <p>Sık kullandığın küçük araçlar artık her sayfada, tek hamle uzağında.</p>
            </div>
            <div className="ft-intro-grid">
              {tools.map(t => (
                <button key={t.id} className="ft-intro-item" onClick={() => { setIntroOpen(false); setActive(t.id); }}>
                  <span className="ft-intro-ic">{t.icon}</span>
                  <span className="ft-intro-txt">
                    <strong>{t.label}</strong>
                    <span>{INTRO_DESC[t.id] || ''}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="ft-intro-tips">
              <span>💡 Kenardaki butonun üzerine gel (mobilde dokun) — araçlar yelpaze gibi açılır</span>
              <span>✋ Butonu sürükleyip dilediğin kenara ve yüksekliğe taşı; konumu hatırlanır</span>
            </div>
            <div className="ft-intro-foot">
              <button className="btn btn-primary" onClick={() => { setIntroOpen(false); setOpen(true); }}>Başlayalım</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Araç modalı: sayfanın üzerinde overlay; rota değişmez ── */}
      {activeTool && (
        <div className="modal-overlay" onClick={() => setActive(null)}>
          <div
            className="modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: activeTool.width, overflow: activeTool.id === 'currency' ? 'visible' : activeTool.id === 'visa' ? 'auto' : 'hidden' }}
          >
            {activeTool.id === 'currency' ? (
              <>
                {/* V71 — Döviz çeviricinin tek girişi (TopBar düğmesi kaldırıldı) */}
                <div className="cc-modal-header">
                  <h3><span className="ft-modal-ic">💱</span> Kur Çevirici</h3>
                  <button className="modal-close" onClick={() => setActive(null)} title="Kapat" aria-label="Kapat">×</button>
                </div>
                <CurrencyConverterMini variant="modal" />
                <div className="cc-modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setActive(null); navigate('/currency'); }}>
                    Tüm Kurlar →
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="ft-modal-head">
                  <span className="ft-modal-ic">{activeTool.icon}</span>
                  <h3>{activeTool.label}</h3>
                  <button className="modal-close" onClick={() => setActive(null)} title="Kapat" aria-label="Kapat">×</button>
                </div>
                <div className="ft-modal-body">
                  {activeTool.id === 'calc' && <CalculatorTool />}
                  {activeTool.id === 'visa' && <VisaTool />}
                  {activeTool.id === 'notes' && <NotesTool uid={uid} />}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

/* V70.151 — TopBar 30sn'de bir bildirim poll'uyla yeniden render olur; FAB'ın
 * bu render'lardan etkilenmemesi için memo (prop yok, yalnız context değişince çizer). */
export default React.memo(FloatingTools);
